import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { PARTNER_BRAND, PARTNER_BRAND_HOVER } from '../Partner/partnerDashboardTheme';
import { EXTRACT_PARTNER_DETAILS_PROMPT_MAX } from './PartnerSettingsSection/companyInfoDefaults';
import { useExtractPartnerDetailsMutation } from '../../api/use-extract-partner-details-mutation';

const PROMPT_PLACEHOLDER =
  'e.g. Wellness Hub Dubai — mental health, personal growth, and connection through workshops and support groups';

const CommunityDescribePrompt = ({ onApply }) => {
  const [prompt, setPrompt] = useState('');
  const extractPartnerDetailsMutation = useExtractPartnerDetailsMutation();
  const isGenerating = extractPartnerDetailsMutation.isPending;

  const handleGenerate = async () => {
    const text = prompt.trim();
    if (!text || isGenerating) return;

    try {
      const result = await extractPartnerDetailsMutation.mutateAsync(text);
      onApply?.(result.formPatch);
      toast.success(result.message || 'Profile filled — review the fields below and the preview.');
    } catch (error) {
      toast.error(error?.message || 'Failed to extract partner details.');
    }
  };

  return (
    <Box
      sx={{
        mb: 2.5,
        p: '18px 20px',
        borderRadius: '16px',
        border: '1.5px solid',
        borderColor: (theme) => theme.portal?.accentMuted ?? theme.palette.divider,
        bgcolor: (theme) => theme.portal?.accentSoft ?? 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <AutoAwesomeIcon sx={{ fontSize: 16, color: PARTNER_BRAND }} />
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'text.primary' }}>
          Describe your community, and we&apos;ll fill in your profile
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 1.25,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder={PROMPT_PLACEHOLDER}
          disabled={isGenerating}
          inputProps={{ maxLength: EXTRACT_PARTNER_DETAILS_PROMPT_MAX }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: 'background.paper',
              fontSize: '0.84375rem',
              '& fieldset': { borderColor: 'divider', borderWidth: 1.5 },
              '&:hover fieldset': { borderColor: 'text.disabled' },
              '&.Mui-focused fieldset': { borderColor: PARTNER_BRAND, borderWidth: 1.5 },
            },
            '& textarea': { overflowY: 'auto !important', resize: 'none' },
          }}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={handleGenerate}
          disabled={!prompt.trim() || isGenerating}
          sx={{
            flexShrink: 0,
            minWidth: 120,
            minHeight: 48,
            px: 2.75,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.84375rem',
            borderRadius: '12px',
            bgcolor: PARTNER_BRAND,
            '&:hover': { bgcolor: PARTNER_BRAND_HOVER },
            '&.Mui-disabled': {
              bgcolor: 'primary.light',
              color: 'common.white',
            },
          }}
        >
          {isGenerating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} sx={{ color: '#fff' }} />
              Filling profile...
            </Box>
          ) : (
            'Generate'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CommunityDescribePrompt;
